import { useReactiveVar, useApolloClient, useLazyQuery, useQuery } from '@apollo/client';
import {
  TiktokVideoAnimationStatusQuery,
  TiktokVideoAnimationStatusQueryVariables,
  VideoAdGenerationStatusQuery,
  VideoAdGenerationStatusQueryVariables,
  VideoNotificationsSettingQuery,
  VideoNotificationsSettingQueryVariables,
} from '__generated__/graphql';
import { config } from 'appConfig';
import { settingsVar } from 'client/cache';
import { deleteCacheField } from 'client/cacheUtils';
import { useCreateOrUpdateFrontendSetting } from 'hooks/mutations/useCreateUpdateFrontendSettings';
import { useExtendedIntl } from 'hooks/useExtendedIntl';
import { useSnackbar } from 'notistack';
import { FunctionComponent, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { isError } from 'typeDeclarations/typeGuards';
import { logError } from 'utils/logging';
import { parseJSONScalar } from 'utils/parseJsonScalar';
import { Setting } from 'utils/Settings/constants';
import { VIDEO_NOTIFICATION_POLL_INTERVAL_MS, VIDEO_NOTIFICATION_TOAST_DURATION_MS } from './constants';
import {
  TIKTOK_VIDEO_ANIMATION_STATUS_QUERY,
  VIDEO_AD_GENERATION_STATUS_QUERY,
  VIDEO_NOTIFICATIONS_SETTING_QUERY,
} from './query';
import {
  EnqueueVideoGenerationNotificationArgs,
  EnqueueVideoNotificationArgs,
  TiktokVideoNotificationItem,
  VideoGenerationNotificationItem,
  VideoNotificationItem,
} from './types';
import { useVideoGenerationToast } from './useVideoGenerationToast';
import { ViewGeneratedVideoButton } from './ViewGeneratedVideoButton';
import { VideoNotificationReadyToast } from './VideoNotificationReadyToast';
import { VideoNotificationsContext } from './VideoNotificationsContext';
import {
  applyVideoGenerationStatus,
  applyVideoNotificationStatus,
  capVideoNotificationHistory,
  createPendingVideoGenerationNotification,
  createPendingVideoNotification,
  dismissVideoNotification,
  getUnreadVideoNotificationCount,
  getVisibleVideoNotifications,
  isVideoGenerationNotification,
  mapAnimationStatus,
  mapGenerationStatus,
  markVideoNotificationRead,
  mergeVideoNotificationItems,
  parseVideoNotificationsSetting,
} from './videoNotifications';

function readStoredItems(): VideoNotificationItem[] {
  return parseVideoNotificationsSetting(settingsVar()[Setting.VideoNotifications]);
}

function writeItemsLocally(nextItems: VideoNotificationItem[]): void {
  if (JSON.stringify(readStoredItems()) === JSON.stringify(nextItems)) {
    return;
  }

  settingsVar({
    ...settingsVar(),
    [Setting.VideoNotifications]: { items: nextItems },
  });
}

function itemsFromSettingValue(value: unknown): VideoNotificationItem[] {
  const parsed = typeof value === 'string' ? parseJSONScalar(value) : value;
  return parseVideoNotificationsSetting(parsed);
}

function itemsFromSettingQuery(data: VideoNotificationsSettingQuery | undefined): VideoNotificationItem[] {
  const value: unknown = data?.sessionUserTeamRelation?.frontendSettings?.edges[0]?.node?.value;
  return itemsFromSettingValue(value);
}

export const VideoNotificationsProvider: FunctionComponent<{ children: ReactNode }> = ({ children }) => {
  const client = useApolloClient();
  const { enqueueSnackbar } = useSnackbar();
  const { formatMessage } = useExtendedIntl();
  const showVideoGenerationToast = useVideoGenerationToast();
  const settings = useReactiveVar(settingsVar);
  const toastedIdsRef = useRef(new Set<string>());
  const persistInFlightRef = useRef(false);
  const persistQueueRef = useRef(Promise.resolve());

  const [createOrUpdateFrontendSetting] = useCreateOrUpdateFrontendSetting({
    onError: (error) => {
      logError(error);
    },
  });

  const items = useMemo(() => parseVideoNotificationsSetting(settings[Setting.VideoNotifications]), [settings]);

  const visibleItems = useMemo(() => getVisibleVideoNotifications(items), [items]);
  const unreadCount = useMemo(() => getUnreadVideoNotificationCount(items), [items]);

  const persistNow = useCallback(
    async (mutator: (current: VideoNotificationItem[]) => VideoNotificationItem[]) => {
      persistInFlightRef.current = true;

      try {
        let remoteItems: VideoNotificationItem[] = [];

        try {
          const result = await client.query<VideoNotificationsSettingQuery, VideoNotificationsSettingQueryVariables>({
            query: VIDEO_NOTIFICATIONS_SETTING_QUERY,
            variables: {
              frontend: config.name,
              name: Setting.VideoNotifications,
            },
            fetchPolicy: 'network-only',
          });

          remoteItems = itemsFromSettingQuery(result.data);
        } catch (error) {
          if (isError(error)) {
            logError(error);
          }
          remoteItems = readStoredItems();
        }

        const nextItems = capVideoNotificationHistory(
          mutator(mergeVideoNotificationItems(readStoredItems(), remoteItems)),
        );

        writeItemsLocally(nextItems);

        await createOrUpdateFrontendSetting({
          variables: {
            input: {
              frontend: config.name,
              name: Setting.VideoNotifications,
              value: JSON.stringify({ items: nextItems }),
            },
          },
        });
      } catch (error) {
        if (isError(error)) {
          logError(error);
        }
      } finally {
        persistInFlightRef.current = false;
      }
    },
    [client, createOrUpdateFrontendSetting],
  );

  const persist = useCallback(
    (mutator: (current: VideoNotificationItem[]) => VideoNotificationItem[]) => {
      persistQueueRef.current = persistQueueRef.current
        .then(() => persistNow(mutator))
        .catch((error: unknown) => {
          if (isError(error)) {
            logError(error);
          }
        });

      return persistQueueRef.current;
    },
    [persistNow],
  );

  const markAsRead = useCallback(
    (id: string) => {
      toastedIdsRef.current.add(id);
      void persist((current) => markVideoNotificationRead(current, id, Date.now()));
    },
    [persist],
  );

  // Closing a toast (its ✕ or the timeout) never marks the item read. Only View video, the "here" link or the bell do.
  const toastVideoGenerationItem = useCallback(
    (item: VideoGenerationNotificationItem) => {
      if (item.status === 'ready') {
        showVideoGenerationToast('ready', {
          key: `video-ready-${item.id}`,
          action: (close) => (
            <ViewGeneratedVideoButton
              item={item}
              label={formatMessage({ id: 'video-notifications.toast.view-video' })}
              onClick={() => {
                markAsRead(item.id);
                close();
              }}
            />
          ),
        });
      } else if (item.status === 'failed') {
        showVideoGenerationToast('failed', { key: `video-failed-${item.id}` });
      }
    },
    [formatMessage, markAsRead, showVideoGenerationToast],
  );

  const toastFinishedItems = useCallback(
    (nextItems: VideoNotificationItem[]) => {
      nextItems.forEach((item) => {
        if (item.status === 'pending' || item.read || toastedIdsRef.current.has(item.id)) {
          return;
        }

        if (isVideoGenerationNotification(item)) {
          toastedIdsRef.current.add(item.id);
          toastVideoGenerationItem(item);
          return;
        }

        // TikTok animations only toast once ready.
        if (item.status !== 'ready') {
          return;
        }

        toastedIdsRef.current.add(item.id);

        enqueueSnackbar(
          formatMessage(
            { id: 'video-notifications.toast.ready' },
            {
              here: (parts: ReactNode[]) => (
                <VideoNotificationReadyToast
                  item={item}
                  onHereClick={() => persist((current) => markVideoNotificationRead(current, item.id, Date.now()))}
                >
                  {parts}
                </VideoNotificationReadyToast>
              ),
            },
          ),
          {
            variant: 'success',
            autoHideDuration: VIDEO_NOTIFICATION_TOAST_DURATION_MS,
            preventDuplicate: true,
            key: `video-ready-${item.id}`,
          },
        );
      });
    },
    [enqueueSnackbar, formatMessage, persist, toastVideoGenerationItem],
  );

  const { data: settingQueryData, error: settingQueryError } = useQuery<
    VideoNotificationsSettingQuery,
    VideoNotificationsSettingQueryVariables
  >(VIDEO_NOTIFICATIONS_SETTING_QUERY, {
    variables: {
      frontend: config.name,
      name: Setting.VideoNotifications,
    },
    fetchPolicy: 'network-only',
    pollInterval: VIDEO_NOTIFICATION_POLL_INTERVAL_MS,
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (settingQueryError) {
      logError(settingQueryError);
      return;
    }

    if (!settingQueryData || persistInFlightRef.current) {
      return;
    }

    const remoteItems = itemsFromSettingQuery(settingQueryData);
    writeItemsLocally(capVideoNotificationHistory(mergeVideoNotificationItems(readStoredItems(), remoteItems)));
  }, [settingQueryData, settingQueryError]);

  useEffect(() => {
    toastFinishedItems(items);
  }, [items, toastFinishedItems]);

  const [fetchAnimationStatus] = useLazyQuery<
    TiktokVideoAnimationStatusQuery,
    TiktokVideoAnimationStatusQueryVariables
  >(TIKTOK_VIDEO_ANIMATION_STATUS_QUERY, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const [fetchGenerationStatus] = useLazyQuery<VideoAdGenerationStatusQuery, VideoAdGenerationStatusQueryVariables>(
    VIDEO_AD_GENERATION_STATUS_QUERY,
    {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
  );

  const pendingKey = items
    .filter((item) => item.status === 'pending')
    .map((item) => item.id)
    .join(',');

  useEffect(() => {
    if (!pendingKey) {
      return undefined;
    }

    const pollAnimationStatus = async (item: TiktokVideoNotificationItem) => {
      const { data, error } = await fetchAnimationStatus({ variables: { id: item.adId } });

      if (error) {
        logError(error);
        return;
      }

      const animation = data?.tiktokSingleVideoAd?.latestImageAnimation;
      const mappedStatus = mapAnimationStatus(animation?.status);

      if (mappedStatus !== 'ready' && mappedStatus !== 'failed') {
        return;
      }

      const animationId = animation?.id ?? item.animationId;

      if (mappedStatus === 'failed') {
        logError(new Error('TikTok video generation failed'), {
          tags: {
            adId: item.adId,
            animationId: animationId ?? '',
            notificationId: item.id,
          },
        });
      }

      await persist((current) => applyVideoNotificationStatus(current, item.id, mappedStatus, animationId, Date.now()));
    };

    const pollGenerationStatus = async (item: VideoGenerationNotificationItem) => {
      const { data, error } = await fetchGenerationStatus({ variables: { id: item.generationId } });

      if (error) {
        logError(error);
        return;
      }

      const generation = data?.videoAdGeneration;
      const mappedStatus = mapGenerationStatus(generation?.status);

      if (mappedStatus !== 'ready' && mappedStatus !== 'failed') {
        return;
      }

      if (mappedStatus === 'failed') {
        logError(new Error('Video generation failed'), {
          tags: {
            generationId: item.generationId,
            advertiserId: item.advertiserId,
            notificationId: item.id,
          },
        });
      }

      // An open library or Add video page refetches straight away instead of waiting for its own poll.
      deleteCacheField({
        cache: client.cache,
        node: { __typename: 'AdvertiserNode', id: item.advertiserId },
        fieldNames: ['videoAdGenerations', 'videoFiles'],
      });

      const videoFileId = generation?.voiceoverVideoFile?.id ?? null;

      await persist((current) => applyVideoGenerationStatus(current, item.id, mappedStatus, videoFileId, Date.now()));
    };

    const pollPendingStatuses = async () => {
      const snapshot = readStoredItems().filter((item) => item.status === 'pending');

      await Promise.all(
        snapshot.map(async (item) => {
          try {
            if (isVideoGenerationNotification(item)) {
              await pollGenerationStatus(item);
            } else {
              await pollAnimationStatus(item);
            }
          } catch (error) {
            if (isError(error)) {
              logError(error);
            }
          }
        }),
      );
    };

    void pollPendingStatuses();
    const intervalId = window.setInterval(() => {
      void pollPendingStatuses();
    }, VIDEO_NOTIFICATION_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [client, fetchAnimationStatus, fetchGenerationStatus, pendingKey, persist]);

  const enqueuePending = useCallback(
    (args: EnqueueVideoNotificationArgs) => {
      const pendingItem = createPendingVideoNotification({
        ...args,
        id: crypto.randomUUID(),
        now: Date.now(),
      });

      void persist((current) => [...current, pendingItem]);
    },
    [persist],
  );

  const enqueuePendingVideoGeneration = useCallback(
    (args: EnqueueVideoGenerationNotificationArgs) => {
      const pendingItem = createPendingVideoGenerationNotification({
        ...args,
        id: crypto.randomUUID(),
        now: Date.now(),
      });

      void persist((current) => [...current, pendingItem]);
    },
    [persist],
  );

  const dismissNotification = useCallback(
    (id: string): void => {
      toastedIdsRef.current.add(id);
      void persist((current) => dismissVideoNotification(current, id));
    },
    [persist],
  );

  const value = useMemo(
    () => ({
      items,
      visibleItems,
      unreadCount,
      enqueuePending,
      enqueuePendingVideoGeneration,
      markAsRead,
      dismissNotification,
    }),
    [dismissNotification, enqueuePending, enqueuePendingVideoGeneration, items, markAsRead, unreadCount, visibleItems],
  );

  return <VideoNotificationsContext.Provider value={value}>{children}</VideoNotificationsContext.Provider>;
};
